import type { ReactNode } from 'react';
import * as _ from 'lodash';
import styles from './IssueDataView.module.less';
import type { ProjectDashboardProject, ProjectDashboardRevision, RevisionRouteParams } from '../projectDashboardTypes';
import { Icon, icons } from '../../generic';
import DonutMetricFacet from '../../DonutMetricFacet';
import useSummaryMetricFacetsData from './useSummaryMetricFacetsData';
import ErrorFacet from '../../DonutMetricFacet/Error';
import { useRouter } from '../../../contexts/RouterContext';

type SummaryMetricFacetProps = {
  label: ReactNode;
  data?: Array<{ name: string; value: number; fill: string }>;
  other?: number;
  total?: number;
  isLoading?: boolean;
  error?: string;
  onClick?: () => void;
};

function createIssuePath(issueType: 'licensing' | 'vulnerability' | 'quality') {
  const { pathname, search } = window.location;
  return `${pathname}/issues/${issueType}${search}`;
}

const SummaryMetricFacet = ({ label, data, other, total, isLoading, error, onClick }: SummaryMetricFacetProps) => {
  // 64 is the radius from the design - this can be tweaked for a larger/smaller donut as needed
  const size = 64;
  return (
    <button className={styles.summaryMetricFacet} onClick={onClick} type="button">
      <DonutMetricFacet
        error={error}
        isLoading={isLoading}
        label={label}
        total={total ?? 0}
        data={data ?? []}
        other={other}
        size={size}
      />
    </button>
  );
};

type IssueDataViewProps = {
  project: ProjectDashboardProject;
  revision: ProjectDashboardRevision;
  params: RevisionRouteParams;
};

const IssueDataView = ({ project, revision, params }: IssueDataViewProps) => {
  const router = useRouter();
  const projectId = params.project_id;
  const revisionId = params.revision_id;
  const revisionScanId = revision?.data?.selectedRevisionScan?.id || revision?.data?.latestRevisionScanId;
  const summaryMetricFacetsData = useSummaryMetricFacetsData(projectId, revisionId, revisionScanId);
  const { isLoading, licensing, quality, security, errors } = summaryMetricFacetsData;
  const isLoadError = (errors?.length ?? 0) !== 0;
  const isRevisionLoading =
    project.status.loading ||
    !revision.data ||
    revision.status.loading ||
    !(revision.data.isSteady || revision.isSteady);
  const isDataOrProjectLoading = isLoading || isRevisionLoading;
  if (isLoadError) {
    return (
      <div className={styles.summaryMetricFacets}>
        <ErrorFacet
          label="Error Loading Project Data"
          message="Something went wrong loading the data for this project"
        />
      </div>
    );
  }
  return (
    <div className={styles.summaryMetricFacets}>
      <SummaryMetricFacet
        label={
          <>
            <Icon icon={icons.scaleBalanced} />
            License Issues
          </>
        }
        data={licensing.data}
        other={licensing.other}
        total={licensing.total}
        isLoading={isDataOrProjectLoading}
        onClick={() => router.push(createIssuePath('licensing'))}
      />
      <SummaryMetricFacet
        label={
          <>
            <Icon icon={icons.bugRegular} />
            Security Issues
          </>
        }
        data={security.data}
        other={security.other}
        total={security.total}
        error={!_.isEmpty(security.errors) ? 'Error loading security issues' : undefined}
        isLoading={isDataOrProjectLoading}
        onClick={() => router.push(createIssuePath('vulnerability'))}
      />
      <SummaryMetricFacet
        label={
          <>
            <Icon icon={icons.scaleBalanced} />
            Quality Issues
          </>
        }
        data={quality.data}
        other={quality.other}
        total={quality.total}
        isLoading={isDataOrProjectLoading}
        onClick={() => router.push(createIssuePath('quality'))}
      />
    </div>
  );
};

export default IssueDataView;
